// app/javascript/controllers/pages_controllers/badumbau_calculator_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    // Steps & Navigation
    "step1", "step2", "step3", "successMessage",
    "step1Indicator", "step2Indicator", "step3Indicator",
    "progressBar",

    // Step 1 Inputs
    "renovationType",

    // Step 2 Inputs & Sections
    "showerThresholdSection", "showerThreshold",
    "tubDoorSection", "tubDoorType",
    "additionalModsSection", "addToilet", "addSink", "addGrabBars",

    // Step 3 Contact Form Inputs & Errors
    "name", "email", "phone", "postalCode", "consent",
    "nameError", "emailError", "phoneError", "postalCodeError", "consentError",

    // Step 3 Price Display
    "totalPrice", "basePriceDisplay", "thresholdDisplay", "tubDoorTypeDisplay", "extrasDisplay"
  ]

  static values = {
    // basePrice: Number // We now calculate base price dynamically, remove this or keep for absolute fallback
  }

  // --- Lifecycle Callbacks ---
  connect() {
    console.log("Badumbau calculator connected");
    this.currentStep = 1;
    this.updateProgressBar();
    this.updateDetailsVisibility();
    this.updatePrice();
  }

  // --- Step Navigation ---
  // nextStep() & previousStep() remain the same

    nextStep() {
    let nextStepTargetElement = null;
    if (!this.hasStep1Target || !this.hasStep2Target || !this.hasStep3Target) {
        console.error("Missing step targets!"); return;
    }
    if (this.currentStep === 1) {
      this.hideElement(this.step1Target); this.currentStep++;
      this.showElement(this.step2Target); nextStepTargetElement = this.step2Target;
    } else if (this.currentStep === 2) {
      this.hideElement(this.step2Target); this.currentStep++;
      this.showElement(this.step3Target); nextStepTargetElement = this.step3Target;
    } else { return; }
    this.updateProgressBar(); this.scrollToTarget(nextStepTargetElement);
  }

  previousStep() {
    let previousStepTargetElement = null;
    if (!this.hasStep1Target || !this.hasStep2Target || !this.hasStep3Target) {
        console.error("Missing step targets!"); return;
    }
    if (this.currentStep > 1) {
      const currentStepTarget = this[`step${this.currentStep}Target`];
      this.hideElement(currentStepTarget); this.currentStep--;
      const previousStepTarget = this[`step${this.currentStep}Target`];
      this.showElement(previousStepTarget); previousStepTargetElement = previousStepTarget;
      this.updateProgressBar(); this.scrollToTarget(previousStepTargetElement);
    }
  }

  // --- UI Updates ---
  // updateProgressBar() & updateIndicatorStyles() remain the same
   updateProgressBar() {
    const progress = (this.currentStep / 3) * 100;
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${progress}%`;
    this.updateIndicatorStyles();
  }

  updateIndicatorStyles() {
      const indicators = [this.step1IndicatorTarget, this.step2IndicatorTarget, this.step3IndicatorTarget];
      indicators.forEach((indicator, index) => {
          if (indicator) {
              const isActive = index + 1 === this.currentStep;
              const isCompleted = index + 1 < this.currentStep;
              indicator.classList.toggle("font-bold", isActive);
              indicator.classList.toggle("text-ph24_new_blue_90", isActive || isCompleted);
              indicator.classList.toggle("text-ph24_blue", !isActive && !isCompleted);
          }
      });
  }

  updateDetailsVisibility() {
    if (!this.hasRenovationTypeTarget || this.renovationTypeTargets.length === 0) return;

    const selectedType = this.getSelectedValue(this.renovationTypeTargets);
    const showShowerDetails = selectedType === 'tub_to_shower' || selectedType === 'new_shower';
    const showTubDoorDetails = selectedType === 'tub_door';

    if (this.hasShowerThresholdSectionTarget) this.toggleElementVisibility(this.showerThresholdSectionTarget, showShowerDetails);
    if (this.hasTubDoorSectionTarget) this.toggleElementVisibility(this.tubDoorSectionTarget, showTubDoorDetails);

    // Ensure default radio checked if section becomes visible
    if (showShowerDetails && this.hasShowerThresholdTarget && !this.getSelectedValue(this.showerThresholdTargets)) {
        (this.showerThresholdTargets.find(t => t.hasAttribute('checked')) || this.showerThresholdTargets[0])?.setAttribute('checked', true);
    }
    if (showTubDoorDetails && this.hasTubDoorTypeTarget && !this.getSelectedValue(this.tubDoorTypeTargets)) {
        (this.tubDoorTypeTargets.find(t => t.hasAttribute('checked')) || this.tubDoorTypeTargets[0])?.setAttribute('checked', true);
    }
    this.updatePrice();
  }


  // --- Price Calculation (UPDATED LOGIC) ---
  updatePrice() {
    // Ensure required targets are present
    if (!this.hasRenovationTypeTarget || !this.hasTotalPriceTarget || !this.hasBasePriceDisplayTarget) {
      console.error("Missing essential targets for price calculation."); return;
    }

    let basePrice = 0;
    let thresholdCost = 0;
    // Tub door cost is now incorporated into base price for that option
    let extrasCost = 0;
    let renovationDescription = "";

    const selectedType = this.getSelectedValue(this.renovationTypeTargets);
    const isShowerType = selectedType === 'tub_to_shower' || selectedType === 'new_shower';

    // 1. Determine Base Price and Description
    switch (selectedType) {
      case 'tub_to_shower':
        basePrice = 4500;
        renovationDescription = "Wanne zu Dusche";
        break;
      case 'new_shower':
        basePrice = 4000;
        renovationDescription = "Neue Dusche";
        break;
      case 'tub_door':
        // Base price depends on the *sub-option* for tub door
        const doorType = this.hasTubDoorTypeTarget ? this.getSelectedValue(this.tubDoorTypeTargets) : 'existing_tub'; // Default if target missing
        if (doorType === 'new_tub_with_door') {
          basePrice = 5500;
          renovationDescription = "Neue Wanne mit Tür";
        } else { // existing_tub
          basePrice = 2500;
          renovationDescription = "Tür in Wanne";
        }
        break;
      default:
        basePrice = 4500; // Default fallback if needed
        renovationDescription = "Basis";
    }

    // 2. Calculate Threshold Cost (only for shower types)
    if (isShowerType && this.hasShowerThresholdTarget) {
      const thresholdType = this.getSelectedValue(this.showerThresholdTargets);
      if (thresholdType === 'level') {
        thresholdCost = 4000; // Cost ON TOP of the shower base price
      }
      // Only show threshold display if it's a shower type
      if (this.hasThresholdDisplayTarget) this.updatePriceDisplay(this.thresholdDisplayTarget, thresholdCost, "Ebenerdig");
       if (this.hasTubDoorTypeDisplayTarget) this.hideElement(this.tubDoorTypeDisplayTarget); // Hide tub door line item
    } else {
      // Hide threshold display if not a shower type
       if (this.hasThresholdDisplayTarget) this.hideElement(this.thresholdDisplayTarget);
       // Show/hide tub door line item based on whether tub door type is selected (it will be 0 if 'existing_tub')
       const doorType = this.hasTubDoorTypeTarget ? this.getSelectedValue(this.tubDoorTypeTargets) : null;
       const tubDoorOptionCost = (doorType === 'new_tub_with_door') ? (5500 - 2500) : 0; // Calculate difference for display only
       if(this.hasTubDoorTypeDisplayTarget) this.updatePriceDisplay(this.tubDoorTypeDisplayTarget, tubDoorOptionCost, "Neue Wanne m. Tür (Aufpreis)");
    }

    // 3. Calculate Extras Cost
    if (this.hasAddToiletTarget && this.addToiletTarget.checked) extrasCost += 800;
    if (this.hasAddSinkTarget && this.addSinkTarget.checked) extrasCost += 700;
    if (this.hasAddGrabBarsTarget && this.addGrabBarsTarget.checked) extrasCost += 300;
    if (this.hasExtrasDisplayTarget) this.updatePriceDisplay(this.extrasDisplayTarget, extrasCost, "Weitere Umbauten");

    // 4. Calculate Total Price
    // Threshold cost is added ON TOP of the base shower price.
    // Tub door cost is handled within the base price selection.
    const totalPrice = basePrice + thresholdCost + extrasCost;

    // 5. Update Price Summary Display
    this.totalPriceTarget.textContent = `${this.formatPrice(totalPrice)}€`;
    this.basePriceDisplayTarget.textContent = `${this.formatPrice(basePrice)}€`;
    const basePriceLabel = this.basePriceDisplayTarget.previousElementSibling;
    if (basePriceLabel) {
      basePriceLabel.textContent = `Umbau (${renovationDescription}):`;
    }
  }


  // --- Form Validation & Submission ---
  // submitForm(), validateForm(), createUserAndLead() remain the same
    submitForm() {
    this.resetErrors();
    if (!this.validateForm()) {
      const firstError = this.element.querySelector('.border-red-500, .ring-red-500');
      if (firstError) this.scrollToTarget(firstError.closest('div') || firstError, 'center');
      return;
    }
    this.createUserAndLead();
  }

  validateForm() {
    let isValid = true;
    if (this.hasNameTarget && !this.nameTarget.value.trim()) isValid = this.showError('name', 'Name ist erforderlich.');
    if (this.hasEmailTarget && !this.validateEmail(this.emailTarget.value.trim())) isValid = this.showError('email', 'Gültige E-Mail erforderlich.');
    if (this.hasPhoneTarget && !this.validatePhoneNumber(this.phoneTarget.value.trim())) isValid = this.showError('phone', 'Gültige Telefonnummer erforderlich.');
    if (this.hasPostalCodeTarget && !this.validateGermanPostalCode(this.postalCodeTarget.value.trim())) isValid = this.showError('postalCode', 'Gültige PLZ (5 Ziffern) erforderlich.');
    if (this.hasConsentTarget && !this.consentTarget.checked) isValid = this.showError('consent', 'Zustimmung erforderlich.', true);
    return isValid;
  }

  createUserAndLead() {
    if (!this.hasNameTarget || !this.hasEmailTarget || !this.hasPhoneTarget || !this.hasPostalCodeTarget || !this.hasRenovationTypeTarget || !this.hasTotalPriceTarget) {
        console.error("Missing required targets for lead creation.");
        alert("Ein interner Fehler ist aufgetreten. Bitte versuchen Sie es später erneut."); return;
    }
    const fullName = this.nameTarget.value.trim();
    const nameParts = fullName.split(' ');
    const productId = 7; // Badumbau Product ID

    const renovationType = this.getSelectedValue(this.renovationTypeTargets);
    let details = '';
     if (renovationType === 'tub_to_shower' || renovationType === 'new_shower') {
        const threshold = this.hasShowerThresholdTarget ? this.getSelectedValue(this.showerThresholdTargets) : 'N/A';
        details += `Dusche Einstieg: ${threshold === 'level' ? 'Ebenerdig' : 'Mit Restschwelle'}\n`;
    } else if (renovationType === 'tub_door') {
        const doorType = this.hasTubDoorTypeTarget ? this.getSelectedValue(this.tubDoorTypeTargets) : 'N/A';
        details += `Badewannentür Art: ${doorType === 'new_tub_with_door' ? 'Neue Wanne mit Tür' : 'In bestehende Wanne'}\n`;
    }
    details += 'Weitere Umbauten:\n';
    details += `  WC (erhöht): ${(this.hasAddToiletTarget && this.addToiletTarget.checked) ? 'Ja' : 'Nein'}\n`;
    details += `  Waschbecken (unterfahrbar): ${(this.hasAddSinkTarget && this.addSinkTarget.checked) ? 'Ja' : 'Nein'}\n`;
    details += `  Haltegriffe: ${(this.hasAddGrabBarsTarget && this.addGrabBarsTarget.checked) ? 'Ja' : 'Nein'}\n`;

    const internalNotes = `Badumbau Kalkulator Anfrage
    ------------------------------
    Hauptumbau: ${this.getRenovationTypeDescription(renovationType)}
    ${details}
    ------------------------------
    Geschätzter Preis: ${this.totalPriceTarget.textContent}
    ------------------------------`;

    const formData = new FormData();
    formData.append('lead[user_attributes][first_name]', nameParts[0] || '');
    formData.append('lead[user_attributes][last_name]', nameParts.slice(1).join(' ') || '');
    formData.append('lead[user_attributes][email]', this.emailTarget.value.trim());
    formData.append('lead[user_attributes][phone]', this.phoneTarget.value.trim());
    formData.append('lead[user_attributes][shipping_address_attributes][zip_code]', this.postalCodeTarget.value.trim());
    formData.append('lead[product_id]', productId);
    formData.append('lead[status]', 'newly_created');
    formData.append('lead[zip_code]', this.postalCodeTarget.value.trim());
    formData.append('lead[internal_notes]', internalNotes.replace(/^ +/gm, ''));

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const submitButton = this.element.querySelector('[data-action="badumbau-calculator#submitForm"]');
    if (submitButton) submitButton.disabled = true;

    fetch('/badumbau_calculator', {
      method: 'POST',
      headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' },
      body: formData
    })
    .then(response => response.ok ? response.json() : response.json().then(err => { throw err; }))
    .then(data => {
      console.log('Success (Badumbau):', data);
      if(this.hasStep3Target) this.hideElement(this.step3Target);
      if(this.hasSuccessMessageTarget) this.showElement(this.successMessageTarget);
      if(this.hasSuccessMessageTarget) this.scrollToTarget(this.successMessageTarget, 'center');
    })
    .catch(error => {
      console.error('Error submitting Badumbau form:', error);
      alert(`Es gab ein Problem: ${error.message || 'Bitte versuchen Sie es erneut.'}`);
      if (submitButton) submitButton.disabled = false;
    });
  }


  // --- Form Reset ---
  // resetForm() remains the same
    resetForm() {
    if(this.hasSuccessMessageTarget) this.hideElement(this.successMessageTarget);
    if(this.hasStep3Target) this.hideElement(this.step3Target);
    if(this.hasStep2Target) this.hideElement(this.step2Target);
    if(this.hasStep1Target) this.showElement(this.step1Target);

    this.currentStep = 1;
    this.updateProgressBar();

    this.element.querySelectorAll('input[type="radio"]').forEach(radio => radio.checked = radio.hasAttribute('checked'));
    this.element.querySelectorAll('input[type="checkbox"]').forEach(checkbox => checkbox.checked = false);
    ['name', 'email', 'phone', 'postalCode'].forEach(field => {
        if (this[`has${field.charAt(0).toUpperCase() + field.slice(1)}Target`]) {
            this[`${field}Target`].value = '';
        }
    });
     if (this.hasConsentTarget) this.consentTarget.checked = false;

    this.updateDetailsVisibility();
    this.resetErrors();
    if(this.hasStep1Target) this.scrollToTarget(this.step1Target);
  }

  // --- Validation Helpers ---
  // validateGermanPostalCode(), validatePhoneNumber(), validateEmail() remain the same
   validateGermanPostalCode = (code) => /^\d{5}$/.test(code);
  validatePhoneNumber = (phone) => /^(0|49|\+49)[1-9]\d{1,14}$/.test(phone.replace(/[\s\-+/()]/g, ''));
  validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // --- Error Handling ---
  // resetErrors() & showError() remain the same
   resetErrors() {
      ['name', 'email', 'phone', 'postalCode', 'consent'].forEach(field => {
           const inputTargetName = `${field}Target`;
           const errorTargetName = `${field}ErrorTarget`;
           const hasInput = this[`has${field.charAt(0).toUpperCase() + field.slice(1)}Target`];
           const hasError = this[`has${field.charAt(0).toUpperCase() + field.slice(1)}ErrorTarget`];

           if (hasInput) {
              const inputTarget = this[inputTargetName];
              inputTarget.classList.remove('border-red-500', 'ring-1', 'ring-red-500');
              if (inputTarget.type === 'checkbox') inputTarget.classList.remove('ring-2');
           }
           if (hasError) {
              const errorTarget = this[errorTargetName];
              errorTarget.textContent = '';
              errorTarget.classList.add('hidden');
           }
      });
  }

  showError(field, message, isCheckbox = false) {
      const inputTargetName = `${field}Target`;
      const errorTargetName = `${field}ErrorTarget`;
      const hasInput = this[`has${field.charAt(0).toUpperCase() + field.slice(1)}Target`];
      const hasError = this[`has${field.charAt(0).toUpperCase() + field.slice(1)}ErrorTarget`];

      if (hasInput) {
          const inputTarget = this[inputTargetName];
          if (isCheckbox) {
               inputTarget.classList.add('ring-2', 'ring-red-500');
          } else {
               inputTarget.classList.add('border-red-500', 'ring-1', 'ring-red-500');
          }
      }
      if (hasError) {
          const errorTarget = this[errorTargetName];
          errorTarget.textContent = message;
          errorTarget.classList.remove('hidden');
      } else {
           console.warn(`Error target missing for field: ${field}`);
      }
      return false;
  }

  // --- Utility Helpers ---
  // formatPrice(), getSelectedValue(), hideElement(), showElement(), toggleElementVisibility(), updatePriceDisplay(), scrollToTarget(), getRenovationTypeDescription() remain the same
    formatPrice = (price) => price.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  getSelectedValue = (targets) => targets?.find(t => t.checked)?.value || null;
  hideElement = (el) => el && el.classList.add('hidden');
  showElement = (el) => el && el.classList.remove('hidden');
  toggleElementVisibility = (el, shouldShow) => el && el.classList.toggle('hidden', !shouldShow);

  updatePriceDisplay(target, amount, labelPrefix = null) {
      if (!target) return;
      const shouldShow = amount !== 0;
      target.classList.toggle("hidden", !shouldShow);
      if (shouldShow) {
          const labelElement = target.previousElementSibling;
          // Check it's the correct span before changing text
          if (labelPrefix && labelElement && labelElement.tagName === 'SPAN' && labelElement.classList.contains('text-gray-600')) {
              labelElement.textContent = `${labelPrefix}:`;
          }
          const valueElement = target.querySelector("span:last-child");
          if (valueElement) {
              const sign = amount > 0 ? "+" : "";
              valueElement.textContent = `${sign}${this.formatPrice(amount)}€`;
          }
      }
  }

   scrollToTarget(target, blockPosition = 'start') {
    if (target) {
      requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: blockPosition }));
    }
  }

  getRenovationTypeDescription(typeValue) {
      switch (typeValue) {
          case 'tub_to_shower': return "Wanne raus, Dusche rein";
          case 'new_shower': return "Neue Dusche (Einbau/Ersatz)";
          case 'tub_door': return "Badewannentür";
          default: return "Unbekannt";
      }
  }
}